/**
 * The shared list-detail layout: a list of rows beside the selected row's detail.
 * Used by What's new (releases) and Configuration (AI processes). Layout only: the
 * caller owns the selection (usually in the URL) and the rows' content.
 *
 *   <ListDetail label="Releases" header={<Search/>} list={rows.map(r => <ListDetailRow .../>)} detail={<Card/>} />
 *
 * The list is a `ul` with one `li` per row (a group heading is a presentational `li`
 * so the rows stay direct children of the list). Below 720px the list becomes a
 * horizontal strip above the detail; every row stays a button, so Tab reaches it.
 */

import type { ReactNode } from 'react';

export function ListDetail({
  label,
  header,
  list,
  detail,
  className,
}: {
  /** Names the list for assistive technology ("Releases"). */
  label: string;
  /** Above the list, outside its scroll (a search field, a count). */
  header?: ReactNode;
  /** `ListDetailRow` and `ListDetailGroup` elements. */
  list: ReactNode;
  detail: ReactNode;
  className?: string;
}) {
  return (
    <div className={`list-detail ${className ?? ''}`.trim()}>
      <div className="list-detail-side">
        {header && <div className="list-detail-header">{header}</div>}
        <ul className="list-detail-list" aria-label={label}>
          {list}
        </ul>
      </div>
      <div className="list-detail-detail">{detail}</div>
    </div>
  );
}

/** A heading between groups of rows (a year). */
export function ListDetailGroup({ children }: { children: ReactNode }) {
  return (
    <li role="presentation" className="list-detail-group">
      <h3>{children}</h3>
    </li>
  );
}

export function ListDetailRow({
  primary,
  secondary,
  trailing,
  selected,
  title,
  onSelect,
}: {
  /** The row's name; truncated with an ellipsis, so give `title` the full text. */
  primary: ReactNode;
  /** A second, quieter line (a date, a model, a highlight). */
  secondary?: ReactNode;
  /** After the name, never truncated (a "Running" word). */
  trailing?: ReactNode;
  selected: boolean;
  /** The full text of what may be truncated. */
  title?: string;
  onSelect: () => void;
}) {
  return (
    <li className="list-detail-item">
      <button
        type="button"
        className="list-detail-row"
        aria-current={selected ? 'true' : undefined}
        title={title}
        onClick={onSelect}
      >
        <span className="list-detail-row-top">
          <span className="list-detail-row-primary">{primary}</span>
          {trailing && <span className="list-detail-row-trailing">{trailing}</span>}
        </span>
        {secondary && <span className="list-detail-row-secondary">{secondary}</span>}
      </button>
    </li>
  );
}
